import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import { Alert } from './feedback';
import { Button, type ButtonVariant } from './controls';

/**
 * Dialoge.
 *
 * Sie ersetzen `window.confirm` und `window.prompt`: die Browserdialoge sehen
 * überall anders aus, lassen sich nicht übersetzen und passen nicht zur
 * übrigen Oberfläche.
 *
 * Ein Dialog erscheint nur bei folgenschweren Schritten - löschen,
 * archivieren, Modus wechseln, Zugang sperren, Konto löschen, Telefoniebereich
 * verlassen. Ein normales Speichern wird nie bestätigt.
 */

export function Modal({
  title,
  onClose,
  footer,
  children,
}: {
  title: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  const titelId = useId();
  const dialog = useRef<HTMLDivElement>(null);

  // Beim Öffnen bekommt der Dialog den Fokus, damit die Tastatur dort landet.
  useEffect(() => {
    const fokussierbar = dialog.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    (fokussierbar ?? dialog.current)?.focus();
  }, []);

  const beiTaste = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialog.current) {
        return;
      }

      // Der Fokus bleibt im Dialog, solange er offen ist.
      const elemente = [
        ...dialog.current.querySelectorAll<HTMLElement>(
          'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled)',
        ),
      ];
      if (elemente.length === 0) {
        return;
      }
      const erstes = elemente[0] as HTMLElement;
      const letztes = elemente[elemente.length - 1] as HTMLElement;

      if (event.shiftKey && document.activeElement === erstes) {
        event.preventDefault();
        letztes.focus();
      } else if (!event.shiftKey && document.activeElement === letztes) {
        event.preventDefault();
        erstes.focus();
      }
    },
    [onClose],
  );

  return (
    <div
      className="dialog-hintergrund"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titelId}
        tabIndex={-1}
        ref={dialog}
        onKeyDown={beiTaste}
      >
        <h2 id={titelId}>{title}</h2>
        {children}
        {footer ? <div className="dialog-aktionen">{footer}</div> : null}
      </div>
    </div>
  );
}

/**
 * Bestätigung eines folgenschweren Schritts.
 *
 * Der Text sagt, was passiert - nicht „Sind Sie sicher?". Die bestätigende
 * Schaltfläche trägt die Handlung als Beschriftung.
 */
export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  tone = 'danger',
  busy,
  error,
  onConfirm,
  onCancel,
  children,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  tone?: ButtonVariant;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  /** Zusätzliche Eingaben, etwa eine Passwortbestätigung. */
  children?: ReactNode;
}): JSX.Element {
  const { t } = useI18n();

  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {t('action.cancel')}
          </Button>
          <Button variant={tone} onClick={onConfirm} disabled={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {error ? <Alert tone="error">{error}</Alert> : null}
      <p>{message}</p>
      {children}
    </Modal>
  );
}
